// Paging helpers for the Transactions page.
// The API returns one page plus an X-Total-Count header.
import { calendarDayKey } from '../../utils/localDate';

import { directionLabel } from '../../utils/transactionDirection';
import { splitSummary } from './splitParts';

export const PAGE_SIZE = 100;
const EXPORT_PAGE = 1000;
const EXPORT_MAX_PAGES = 200; // hard stop at 200k rows

// Keeps the first row for each id (a row must never show twice).
export function uniqueById(rows) {
  const seen = new Set();
  return (rows || []).filter((r) => {
    if (r?.id === undefined || r?.id === null) return true;
    if (seen.has(r.id)) return false;
    seen.add(r.id);
    return true;
  });
}

export function totalFrom(response) {
  const raw = response && response.headers && (response.headers['x-total-count'] ?? response.headers['X-Total-Count']);
  const n = Number(raw);
  return Number.isFinite(n) && raw !== undefined && raw !== null ? n : null;
}

// Fetches every row that matches the filters, one page at a time.
export async function fetchAllTransactions(getPage, params) {
  const rows = [];
  for (let page = 0; page < EXPORT_MAX_PAGES; page += 1) {
    const res = await getPage({ ...params, limit: EXPORT_PAGE, offset: page * EXPORT_PAGE });
    const batch = Array.isArray(res.data) ? res.data : [];
    rows.push(...batch);
    const total = totalFrom(res);
    if (batch.length < EXPORT_PAGE || (total !== null && rows.length >= total)) break;
  }
  return rows;
}

function cell(value) {
  if (value === null || value === undefined || value === '') return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(records) {
  if (!records.length) return '';
  const headers = Object.keys(records[0]);
  return [headers.join(','), ...records.map((row) => headers.map((h) => cell(row[h])).join(','))].join('\n');
}

// One export row: read-only IDs first, editable fields on the right
// (the Update-from-Excel import reads this layout back).
// Export shows the stored direction, so an Update Existing round trip
// sees what is saved (income with a stale 'out' shows Out, not In).
export function exportDirection(t) {
  const saved = String(t?.direction || '').toLowerCase();
  if (t?.transaction_type !== 'transfer' && Number(t?.amount) >= 0 && (saved === 'in' || saved === 'out')) {
    return saved === 'in' ? 'In' : 'Out';
  }
  return directionLabel(t);
}

export function exportRecord(t) {
  return {
    'ID': t.id,
    'Business ID': t.business_id || '',
    'Category ID': t.category_id || '',
    'Account ID': t.account_id || '',
    'State Registration ID': t.state_registration_id || '',
    'Date': t.transaction_date ? calendarDayKey(t.transaction_date) : '',
    'Description': t.description || '',
    'Category': t.category_name || '',
    'Amount': t.amount !== null && t.amount !== undefined ? t.amount : '',
    'Type': t.transaction_type || '',
    'Direction': t.transaction_type ? exportDirection(t) : '',
    'Reference': t.reference_number || '',
    'Notes': t.notes || '',
    'Split Parts': t.splits?.length ? splitSummary(t.splits) : '',
    'Bank Transaction ID': t.plaid_transaction_id || ''
  };
}
